// ===================================================================
// Catálogo: tipos de atendimento especial (instituição)
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

const emptyForm = {
  name: '',
  description: '',
  category: 'specialized',
  max_extra_minutes: '',
  requires_attachment: true,
  is_active: true,
};

export const SpecialAttendanceTypesManager = React.memo(() => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { addNotification } = useNotifications();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSpecialAttendanceTypes({ activeOnly: false });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      addNotification('Erro', 'Não foi possível carregar os tipos', 'error');
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      description: t.description || '',
      category: t.category || 'specialized',
      max_extra_minutes: t.max_extra_minutes != null ? String(t.max_extra_minutes) : '',
      requires_attachment: t.requires_attachment !== false,
      is_active: t.is_active !== false,
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      addNotification('Validação', 'Nome é obrigatório', 'error');
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      max_extra_minutes: form.max_extra_minutes === '' ? null : parseInt(form.max_extra_minutes, 10),
      requires_attachment: Boolean(form.requires_attachment),
      is_active: Boolean(form.is_active),
    };
    try {
      if (editing) {
        await apiClient.updateSpecialAttendanceType(editing.id, payload);
        addNotification('Sucesso', 'Tipo atualizado', 'success');
      } else {
        await apiClient.createSpecialAttendanceType(payload);
        addNotification('Sucesso', 'Tipo criado', 'success');
      }
      setModal(false);
      load();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao salvar', 'error');
    }
  };

  if (loading && rows.length === 0) {
    return <LoadingSpinner text="Carregando tipos..." />;
  }

  return (
    <div style={{ ...styles.contentContainer, padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={styles.cardTitle}>Tipos de atendimento especial</h2>
        <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={openNew}>Novo tipo</button>
      </div>
      <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
        Cadastro institucional. Depois, vincule os tipos a cada processo por prova em Processos.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: '#F3F4F6' }}>
            <th style={{ padding: 8, textAlign: 'left' }}>Nome</th>
            <th style={{ padding: 8 }}>Categoria</th>
            <th style={{ padding: 8 }}>Ativo</th>
            <th style={{ padding: 8 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
              <td style={{ padding: 8 }}>{t.name}</td>
              <td style={{ padding: 8 }}>{t.category === 'specific' ? 'Específico' : 'Especializado'}</td>
              <td style={{ padding: 8 }}>{t.is_active ? 'Sim' : 'Não'}</td>
              <td style={{ padding: 8 }}>
                <button type="button" style={{ ...styles.button, ...styles.buttonSecondary }} onClick={() => openEdit(t)}>Editar</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Editar tipo' : 'Novo tipo'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={styles.label}>Nome *</label>
          <input style={styles.input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <label style={styles.label}>Descrição</label>
          <textarea style={{ ...styles.input, minHeight: 60 }} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <label style={styles.label}>Categoria</label>
          <select style={styles.select} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
            <option value="specialized">Especializado</option>
            <option value="specific">Específico</option>
          </select>
          <label style={styles.label}>Tempo extra máx. (minutos, opcional)</label>
          <input style={styles.input} value={form.max_extra_minutes} onChange={(e) => setForm((f) => ({ ...f, max_extra_minutes: e.target.value }))} />
          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.requires_attachment} onChange={(e) => setForm((f) => ({ ...f, requires_attachment: e.target.checked }))} />
            Exige anexo
          </label>
          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            Ativo
          </label>
          <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={save}>Salvar</button>
        </div>
      </Modal>
    </div>
  );
});
